import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Hammer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../lib/auth';
import { api, extractError } from '../lib/api';

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/system/status');
        if (!r.data.initialized) setNeedsOnboarding(true);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (user) navigate(user.force_password_change ? '/profil' : '/', { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Willkommen, ${u.first_name}!`);
      navigate(u.force_password_change ? '/profil' : '/', { replace: true });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  if (needsOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 w-12 h-12 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Hammer size={24} /></div>
            <CardTitle className="font-display">Erster Start</CardTitle>
            <CardDescription>Die App ist noch nicht eingerichtet. Legen Sie den ersten Administrator an.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/onboarding')} className="w-full" data-testid="start-onboarding-button">Einrichtung starten</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-white to-slate-100">
      
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader>
            <CardTitle className="font-display text-2xl">Anmelden</CardTitle>
            <CardDescription>Bitte mit Ihren Zugangsdaten anmelden.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">E-Mail-Adresse oder Benutzername</Label>
                {/* type="text" statt "email": Monteure melden sich mit ihrem
                    Benutzernamen an, den der Browser sonst als ungültige
                    Adresse abweisen würde. */}
                <Input id="email" type="text" autoComplete="username" required value={email}
                  onChange={e => setEmail(e.target.value)} data-testid="login-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Input id="password" type={show ? 'text' : 'password'} autoComplete="current-password" required value={password}
                    onChange={e => setPassword(e.target.value)} data-testid="login-password-input" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-900" aria-label="Passwort anzeigen">
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={remember} onCheckedChange={v => setRemember(!!v)} data-testid="login-remember-checkbox" />
                <span>Angemeldet bleiben</span>
              </label>
              <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-button">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Anmelden
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
